import * as util from 'util';
import {DICTIONARY} from '../../../../dictionary';
import {RouteService} from '../../../../services/routeService';
import {NotificationService} from '../../../../services/notificationService';
import {GeneralService} from '../../../../services/generalService';
import {Component, OnInit} from "@angular/core";

// CC: standalone duplicate of user-permissions.component.ts -> UserPermissionsComponent (multi tenant tab - not reused, meant to be modified independently)
@Component({
	selector: 'user-permissions-multi-tenant-component',
	templateUrl: './user-permissions-multi-tenant.component.html',
})
export class UserPermissionsMultiTenantComponent implements OnInit {
    dic = DICTIONARY;
    reviewersList = [];
	planModules
	planUsers;
	// CC: per-type flag of whether the module is enabled in the plan (keyed by reviewerType/tab key). Computed from planModules
	moduleEnabled = {};
	loadingReviewers = false;
	addReviewerPopup;
	filterData;

	// CC: global reviewer modules & sub-permissions - taken from user-management.component.html -> globalReviewer tab
	reviewerTypes = [
		{
			display: 'Inbound Shield', key: 'inbound', selected: false, expanded: false, iconClass: 'far fa-user-shield',
			description: 'Reviewers can view quarantined emails for all of your managed plans and clients, as well as take actions',
			permissions: [
				{field: 'reviewer_allow_release_malicious', label: 'Release Malicious', selected: false, description: 'Allow this reviewer to release malicious emails'},
				{field: 'allow_view_content', label: 'View Content', selected: false, description: 'Allow this reviewer to view quarantine email content'},
				{field: 'allow_change_configuration', label: 'Change Configuration', selected: false, description: 'Allow this reviewer to change configurations and settings'},
			]
		},
		{
			display: 'Outbound Shield', key: 'outbound', selected: false, expanded: false, iconClass: 'far fa-shield',
			description: 'Reviewers will have access to outbound setting, rules, and policies for all of your managed plans and clients',
			permissions: [
				{field: 'allow_view_content', label: 'View Content', selected: false, description: 'Allow this reviewer to view quarantine email content'},
				{field: 'allow_change_configuration', label: 'Change Configuration', selected: false, description: 'Allow this reviewer to change configurations and settings'},
			]
		},
		{
			display: 'Simulation and Training', key: 'threat_simulation', selected: false, expanded: false, iconSvgName: 'double-hook.svg',
			description: 'Reviewers will have access to all Threat Simulation pages, and will be able to send Threat Simulation campaigns and view campaign metrics',
			permissions: []
		},
		{
			display: 'Account Takeover', key: 'account_compromised', selected: false, expanded: false, iconClass: 'far fa-user-secret',
			description: 'Reviewers will have access to information regarding Account Takeover incidents and will receive notifications for such incidents',
			permissions: []
		},
		{
			display: 'Archive', key: 'archive', selected: false, expanded: false, iconClass: 'far fa-archive',
			description: 'Reviewers who will have access to archive information',
			permissions: [
				{field: 'allow_view_content', label: 'View Content', selected: false, description: 'Allow this reviewer to view archived email content'},
			]
		},
	];

	// CC: plan-admin level permissions (not per-module) - shown compactly in the "Admin" column. field matches the server adminPermissions object
	// CC: action maps the field to the doActionOnPlanAdmin action (same mapping as modifyAdminPermission)
	adminPermissionTypes = [
		{field: 'enabled', label: 'Admin', iconClass: 'far fa-user-shield', iconColor: '#337ab7', action: this.dic.CONSTANTS.planAdminActions.subAdmin, selected: false, description: 'Admin permissions empower the user to navigate through and modify the complete plan configuration across all pages'},
		// CC: read_only is a restriction (not a granted capability) - use red to distinguish it from the blue permission icons
		{field: 'read_only', label: 'Read Only', iconClass: 'fas fa-eye', iconColor: '#d9534f', action: this.dic.CONSTANTS.planAdminActions.readOnly, selected: false, description: 'Admin will have read-only access with no ability to make changes'},
		{field: 'show_multi_tenant', label: 'Multi Tenant View', iconClass: 'far fa-layer-group', iconColor: '#337ab7', action: this.dic.CONSTANTS.planAdminActions.multiTenantView, selected: false, description: 'Admin will have access to the multi-tenant selector, which allows them to directly access managed plans and clients'},
		// CC: technician role - full admin on managed plans only (provisioning, multi tenant, copy policy), no admin access to the own plan
		{field: 'managed_plans_only', label: 'Technician (managed plans only)', iconClass: 'far fa-user-cog', iconColor: '#337ab7', action: this.dic.CONSTANTS.planAdminActions.managedPlansOnly, selected: false, description: 'Admin permissions apply to managed plans only: the user can provision plans, manage them through the multi-tenant view and copy policies, but has no admin access to your own plan'},
	];

	// CC: admin section for the add-reviewer wizard - mirrors a reviewerTypes checkbox row.
	// CC: master checkbox grants plan-admin (subAdmin); its permissions are the admin sub-permissions (excluding the master 'enabled' field)
	adminWizardSection = {
		display: 'Admin', selected: false, expanded: false, iconClass: 'far fa-user-shield',
		description: 'Admin permissions empower the user to navigate through and modify the complete plan configuration across all pages',
		permissions: this.adminPermissionTypes.filter(perm => perm.field !== 'enabled')
	};

	constructor(private gs:GeneralService,
				private rs:RouteService,
				private ns:NotificationService) {
    }

	ngOnInit() {
		this.initFilters();
        this.getPlanReviewers();
    };

	// CC: taken from user-management.component.ts -> initFilters
	initFilters = () => {
		this.filterData = {
			filterType: this.dic.CONSTANTS.tableFilters.userPermissions,
			filters: {
				reviewer: ['Admin', 'Inbound Shield', 'Outbound Shield', 'Simulation and Training', 'Account Takeover', 'Archive']
			},
			initFiltersFunction: this.initFilters
		};
	};

	getPlanReviewers = () => {
		this.loadingReviewers = true;
		this.rs.getPlanReviewers().then((response) => {
			this.reviewersList = response?.globalReviewers;
			this.planModules = response.modules;
			this.planUsers = response.users;
			// CC: admins & sub-admins implicitly have access to all modules - flag it so the table can show a crown indicator
			this.reviewersList?.forEach(reviewer => {
				reviewer.isPlanAdmin = [this.dic.CONSTANTS.userRole.admin, this.dic.CONSTANTS.userRole.subAdmin].includes(reviewer.adminPermissions?.user_role);
			});
			this.setModulesEnabledState();
			this.loadingReviewers = false;
		}, (err) => {
			this.loadingReviewers = false;
		});
	};

	// CC: map this component's type/tab keys to the plan module keys returned in getPlanReviewers.modules,
	// CC: so a module disabled in the plan can be blocked from receiving reviewers
	setModulesEnabledState = () => {
		// CC: inbound, outbound and account takeover are always enabled - only these plan-dependent modules can be disabled
		const planKeyByTypeKey = {
			threat_simulation: 'threat_simulation',
			archive: 'archive'
		};
		this.moduleEnabled = {};
		this.reviewerTypes.forEach(type => {
			// CC: if plan modules info is missing entirely, don't block (default enabled)
			this.moduleEnabled[type.key] = !planKeyByTypeKey[type.key] || (this.planModules ? !!this.planModules[planKeyByTypeKey[type.key]] : true);
		});
	};

	// CC: export the main table by module only (Enabled/Disabled per module, no sub-permissions) - taken from user-management.component.ts -> exportUsersCsv
	exportReviewersCsv = (sortBy) => {
		if (!this.reviewersList || !this.reviewersList.length) {
			this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
			return;
		}

		let csvString = 'Email,Admin,' + this.reviewerTypes.map(type => type.display).join(',') + '\n';

		let sortedTable = this.reviewersList.filter(reviewer => !reviewer.hide);
		if (sortBy) {
			sortedTable = this.gs.sortTable(sortedTable, sortBy);
		}

		sortedTable.forEach(reviewer => {
			const modules = this.reviewerTypes.map(type => reviewer[type.key]?.enabled ? 'Enabled' : 'Disabled');
			// CC: Admin column reflects the plan-admin flag (admin/sub-admin) computed in getPlanReviewers
			csvString += `${reviewer.email},${!!reviewer.isPlanAdmin},${modules.join(',')}\n`;
		});

		this.gs.exportCsv(csvString, 'user_permissions.csv');
	};

	// CC: taken from user-management.component.ts -> suppressionPopup.search
	searchReviewer = (searchTerm, activeFilters) => {
		searchTerm = (searchTerm || '').toLowerCase();

		this.reviewersList.forEach(record => {
			// search
			if (searchTerm && !record.email.toLowerCase().includes(searchTerm)) {
				record.hide = true;
				return;
			}

			// filter
			if (activeFilters && !this.filterReviewerExecute(record, activeFilters)) {
				record.hide = true;
				return;
			}

			record.hide = false;
		});
	};

	// CC: taken from user-management.component.ts -> searchFilterExecute
	filterReviewerExecute = (reviewer, filters) => {
		// need to match all filter types
		let numFilterToMatch = Object.keys(filters).length;

		if (filters.reviewer?.length) {
			if ((filters.reviewer.includes('Admin') && reviewer.isPlanAdmin) ||
				(filters.reviewer.includes('Inbound Shield') && reviewer.inbound?.enabled) ||
				(filters.reviewer.includes('Outbound Shield') && reviewer.outbound?.enabled) ||
				(filters.reviewer.includes('Simulation and Training') && reviewer.threat_simulation?.enabled) ||
				(filters.reviewer.includes('Account Takeover') && reviewer.account_compromised?.enabled) ||
				(filters.reviewer.includes('Archive') && reviewer.archive?.enabled)) {
				numFilterToMatch--;
			}
		}

		return !numFilterToMatch;
	};

	// CC: taken from email-trace.component.ts -> showLogsAction (toggle expandable sub-row on row click)
	toggleReviewerExpand = (reviewer) => {
		reviewer.isExpanded = !reviewer.isExpanded;
		// CC: collapsing the row completes the inline edit - return to regular (read-only) mode
		if (!reviewer.isExpanded) {
			reviewer.editConfig = false;
		}
	};

	// CC: taken from user-management.component.ts -> showUserActions
	showReviewerActions = (reviewer) => {
		const actions = [this.dic.CONSTANTS.reviewerPermissionsActions.modifyPermissions];
		// CC: a full admin's reviewer permissions can't be removed here - hide the Remove action for them
		if (reviewer.adminPermissions?.user_role !== this.dic.CONSTANTS.userRole.admin) {
			actions.push(this.dic.CONSTANTS.reviewerPermissionsActions.delete);
		}
		return actions;
	};

	// CC: taken from user-management.component.ts -> selectUserAction
	selectReviewerAction = (reviewer, action) => {
		switch (action) {
			case this.dic.CONSTANTS.reviewerPermissionsActions.modifyPermissions:
				this.openModifyPermissions(reviewer);
				break;

			case this.dic.CONSTANTS.reviewerPermissionsActions.delete:
				this.deleteReviewer(reviewer);
				break;
		}
	};

	// CC: taken from user-management.component.ts -> showMultipleUsersActions
	showBulkReviewerActions = () => {
		return [this.dic.CONSTANTS.reviewerPermissionsActions.delete];
	};

	// CC: taken from user-management.component.ts -> selectMultipleUsersAction
	selectBulkReviewerAction = (reviewers, action) => {
		switch (action) {
			case this.dic.CONSTANTS.reviewerPermissionsActions.delete:
				this.deleteReviewers(reviewers);
				break;
		}
	};

	// CC: "Modify permissions" now expands the reviewer's row inline and switches it to edit mode (no popup)
	openModifyPermissions = (reviewer) => {
		reviewer.isExpanded = true;
		reviewer.editConfig = true;
	};

	// CC: taken from user-management.component.ts -> modifyUserPermissionsExecute / modifyUserReadOnlyExecute / modifyUserMultiTenantViewExecute
	// CC: reviewer is passed in (popup or inline-edit row) and locked via reviewer.inProcess so both callers share this
	modifyAdminPermission = (reviewer, field) => {
		if (reviewer.inProcess) {
			return;
		}
		reviewer.inProcess = true;

		const adminPerms = reviewer.adminPermissions;

		let enabled, action;
		switch (field) {
			case 'enabled':
				enabled = adminPerms.enabled;
				action = this.dic.CONSTANTS.planAdminActions.subAdmin;
				break;

			case 'read_only':
				enabled = adminPerms.read_only;
				action = this.dic.CONSTANTS.planAdminActions.readOnly;
				break;

			case 'show_multi_tenant':
				enabled = adminPerms.show_multi_tenant;
				action = this.dic.CONSTANTS.planAdminActions.multiTenantView;
				break;

			case 'managed_plans_only':
				enabled = adminPerms.managed_plans_only;
				action = this.dic.CONSTANTS.planAdminActions.managedPlansOnly;
				break;
		}

		const statusUpdate = {
			enabled: enabled,
			action: action,
			depth: reviewer.depth,
			parent: reviewer.parent
		};
		this.rs.doActionOnPlanAdmin(reviewer.email, statusUpdate).then((response) => {
			if (field === 'enabled') {
				// CC: keep user_role in sync and clear admin-only permissions when admin is revoked
				adminPerms.user_role = enabled ? this.dic.CONSTANTS.userRole.subAdmin : this.dic.CONSTANTS.userRole.user;
				if (!enabled) {
					adminPerms.read_only = false;
					adminPerms.show_multi_tenant = false;
					adminPerms.managed_plans_only = false;
				}
			}
			// CC: the server forces multi tenant view on for technicians
			if (field === 'managed_plans_only' && enabled) {
				adminPerms.show_multi_tenant = true;
				// CC: keep the table crown indicator in sync with the new role (same logic as getPlanReviewers)
				reviewer.isPlanAdmin = [this.dic.CONSTANTS.userRole.admin, this.dic.CONSTANTS.userRole.subAdmin].includes(adminPerms.user_role);
			}
			this.ns.showInfoMessage(response.log);
			reviewer.inProcess = false;
		}, () => {
			// CC: revert toggle on failure
			adminPerms[field] = !enabled;
			reviewer.inProcess = false;
		});
	};

	// CC: build & send a global-reviewer update for a single module - taken from user-management.component.ts -> updateUserGlobalReviewerExecute
	updateGlobalReviewer = (reviewer, type) => {
		const actionInfo: any = {
			type: type,
			action: this.dic.CONSTANTS.planUsersActions.globalReviewer,
			isAdmin: reviewer.adminPermissions?.user_role === this.dic.CONSTANTS.userRole.admin,
			depth: reviewer.depth,
			parent: reviewer.parent,
			enabled: reviewer[type]?.enabled
		};

		switch (type) {
			case 'inbound':
				actionInfo.reviewer_allow_release_malicious = reviewer[type].reviewer_allow_release_malicious;
				actionInfo.allow_view_content = reviewer[type].allow_view_content;
				actionInfo.allow_change_configuration = reviewer[type].allow_change_configuration;
				break;

			case 'outbound':
				actionInfo.allow_view_content = reviewer[type].allow_view_content;
				actionInfo.allow_change_configuration = reviewer[type].allow_change_configuration;
				break;

			case 'archive':
				actionInfo.allow_view_content = reviewer[type].allow_view_content;
				break;
		}

		return this.rs.doActionOnPlanUser(reviewer.email, actionInfo);
	};

	// CC: toggle a whole global-reviewer module on/off - taken from user-management.component.ts -> updateUserGlobalReviewerExecute
	// CC: reviewer is passed in (popup or inline-edit row) and locked via reviewer.inProcess so both callers share this
	modifyReviewerPermission = (reviewer, typeKey) => {
		if (reviewer.inProcess) {
			return;
		}
		reviewer.inProcess = true;

		const email = reviewer.email;
		const enabled = reviewer[typeKey].enabled;

		this.updateGlobalReviewer(reviewer, typeKey).then(() => {
			if (enabled) {
				this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemAdded, 'Reviewer'));
			}
			else {
				this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemDeleted, 'Reviewer', email));
			}
			reviewer.inProcess = false;
		}, () => {
			// CC: revert toggle on failure
			reviewer[typeKey].enabled = !enabled;
			reviewer.inProcess = false;
		});
	};

	// CC: toggle a single global-reviewer sub-permission - taken from user-management.component.ts -> updateUserGlobalReviewerExecute
	// CC: reviewer is passed in (popup or inline-edit row) and locked via reviewer.inProcess so both callers share this
	modifySubPermission = (reviewer, tab, permission) => {
		if (reviewer.inProcess) {
			return;
		}
		reviewer.inProcess = true;

		const module = reviewer[tab.key];
		const enabled = module[permission.field];

		this.updateGlobalReviewer(reviewer, tab.key).then(() => {
			this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
			reviewer.inProcess = false;
		}, () => {
			// CC: revert toggle on failure
			module[permission.field] = !enabled;
			reviewer.inProcess = false;
		});
	};

	openAddReviewerWizard = () => {
		// CC: reset any prior selection/expansion so the wizard always starts clean
		this.reviewerTypes.forEach(type => {
			type.selected = false;
			type.expanded = false;
			type.permissions.forEach(permission => {
				permission.selected = false;
			});
		});

		// CC: reset the admin section selection
		this.adminWizardSection.selected = false;
		this.adminWizardSection.expanded = false;
		this.adminWizardSection.permissions.forEach(perm => {
			perm.selected = false;
		});

		this.planUsers.forEach(user => {
			user.selected = false;
		});

		this.addReviewerPopup = {
			show: true,
			step: 1,
			loadingUsers: false,
			users: this.planUsers,
			inProcess: false
		};
	};

	// CC: taken from users-selection.component.ts -> searchUsers
	searchWizardUsers = (searchTerm) => {
		searchTerm = (searchTerm || '').toLowerCase();
		this.addReviewerPopup.users.forEach(user => {
			user.hide = searchTerm && !user.email.toLowerCase().includes(searchTerm) && !(user.name || '').toLowerCase().includes(searchTerm);
		});
	};

	goToReviewerTypesStep = () => {
		if (!this.addReviewerPopup.users.some(user => user.selected)) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.selectAtLeastOneX, 'user'));
			return;
		}
		this.addReviewerPopup.step = 2;
	};

	submitNewReviewer = () => {
		if (this.addReviewerPopup.inProcess) {
			return;
		}

		const selectedUsers = this.addReviewerPopup.users.filter(user => user.selected);
		const selectedTypes = this.reviewerTypes.filter(type => type.selected);
		if (!selectedTypes.length && !this.adminWizardSection.selected) {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.selectAtLeastOneX, 'reviewer type or admin'));
			return;
		}

		this.addReviewerPopup.inProcess = true;

		// CC: enabling a global-reviewer module (with its sub-permission flags) is idempotent -
		// CC: doActionOnPlanUser with enabled:true both adds a new reviewer and updates an existing one
		const tasks = [];
		selectedUsers.forEach(user => {
			const existingReviewer = this.reviewersList.find(reviewer => reviewer.email === user.email);

			// CC: grant plan-admin (and its selected sub-permissions) when the admin section is selected
			if (this.adminWizardSection.selected) {
				tasks.push(this.addAdminRequest(user, existingReviewer));
			}

			selectedTypes.forEach(type => {
				const flags = {};
				type.permissions.forEach(permission => {
					flags[permission.field] = !!permission.selected;
				});
				tasks.push(this.addReviewerTypeRequest(type.key, user, flags));
			});
		});

		Promise.all(tasks).then(() => {
			this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemAdded, 'Reviewer'));
			this.addReviewerPopup = null;
			this.getPlanReviewers();
		}, () => {
			this.addReviewerPopup.inProcess = false;
		});
	};

	// CC: enable a global-reviewer module for a user with its sub-permission flags - taken from user-management.component.ts -> updateUserGlobalReviewerExecute
	addReviewerTypeRequest = (typeKey, user, flags) => {
		const actionInfo: any = {
			type: typeKey,
			action: this.dic.CONSTANTS.planUsersActions.globalReviewer,
			isAdmin: user.user_role === this.dic.CONSTANTS.userRole.admin,
			depth: user.depth,
			parent: user.parent,
			enabled: true,
			...flags
		};

		return this.rs.doActionOnPlanUser(user.email, actionInfo);
	};

	// CC: grant plan-admin (subAdmin) with the selected sub-permissions bundled as flags (mirrors modifyAdminPermission)
	addAdminRequest = (user, existingReviewer) => {
		// CC: existingReviewer carries depth/parent needed by doActionOnPlanAdmin - fall back to the wizard user
		const target = existingReviewer || user;

		// CC: a full admin's permissions can't be modified here - skip the request
		if (existingReviewer?.adminPermissions?.user_role === this.dic.CONSTANTS.userRole.admin) {
			return Promise.resolve();
		}

		// CC: flags map the selected admin sub-permissions to their fields (read_only / show_multi_tenant)
		const flags = {};
		this.adminWizardSection.permissions.forEach(perm => {
			if (perm.selected) {
				flags[perm.field] = true;
			}
		});

		const statusUpdate = {
			enabled: true,
			action: this.dic.CONSTANTS.planAdminActions.subAdmin,
			depth: target.depth,
			parent: target.parent,
			flags: flags
		};
		return this.rs.doActionOnPlanAdmin(target.email, statusUpdate);
	};

	// CC: taken from region.component.ts -> changeRegion (gs.showPopup confirmation pattern)
	deleteReviewer = (reviewer) => {
		this.gs.showPopup({
			title: 'Remove Reviewer',
			subTitle: `Please note - you are about to remove reviewer permissions for ${reviewer.email}`,
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Remove',
			doneCb: () => {
				// CC: globalReviewer=true tells the server this is a multi-tenant global reviewer removal
				this.rs.deleteReviewer(reviewer.email, {globalReviewer: true}).then(() => {
					this.reviewersList = this.reviewersList.filter(r => r !== reviewer);
					this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemRemoved, 'Reviewer'));
				});
			},
		});
	};

	// CC: taken from region.component.ts -> changeRegion (gs.showPopup confirmation pattern)
	deleteReviewers = (reviewers) => {
		this.gs.showPopup({
			title: 'Remove Reviewers',
			subTitle: `Please note - you are about to remove reviewer permissions for ${reviewers.length} users`,
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Remove',
			doneCb: () => {
				// CC: globalReviewer=true tells the server this is a multi-tenant global reviewer removal
				Promise.all(reviewers.map(reviewer => this.rs.deleteReviewer(reviewer.email, {globalReviewer: true}))).then(() => {
					this.reviewersList = this.reviewersList.filter(r => !reviewers.includes(r));
					this.ns.showInfoMessage(util.format(this.dic.MESSAGES.itemRemoved, 'Reviewers'));
				});
			},
		});
	};
}
